import React from 'react'

import { Formik } from 'formik'
import queryString from 'query-string'
import { useLocation } from 'react-router-dom'

import { ZariContext } from '_zari/context'

import { Button, Col, Divider, Row, Form } from '_zari/core'
import Yup from '_zari/validation/_yup'

import { PeriodoContext } from 'contexts/PeriodoContext'

import { OrigenesContabilidadConstant } from 'constants/index'

import { IdGen, KeyEventUtil, ZrSwal } from 'utils'

import * as PlantillaGeneralService from 'services/plantillaGeneralContabilidad.service'
import * as AsientosContablesService from 'services/asientosContables.service'

import * as FormGroups from './formGroups'
import * as Sections from './sections'
import { AdminContext } from 'contexts/AdminContext'

const commonReducer = (state, action) => {
  switch (action.type) {
    case 'agregar':
      return [
        ...state,
        {
          ...action.payload,
          _idFila: IdGen.newId(),
          _esNuevo: true
        }
      ]
    case 'actualizar':
      return state.map(val => {
        if (val._idFila === action.payload._idFila) {
          val = {
            ...val,
            ...action.payload
          }
        }
        return val
      })
    case 'fill':
      return action.payload.map(x => ({
        ...x,
        _idFila: IdGen.newId()
      }))
    case 'append':
      const nuevosRegistros = action.payload.map(x => ({
        ...x,
        _idFila: IdGen.newId(),
        _esNuevo: false,
        modalValido: true
      }))
      return [...state, ...nuevosRegistros]
    case 'eliminar':
      return state.filter(val => val._idFila !== action.payload._idFila)
    default:
      return state
  }
}

export const ValidationSchema = Yup.object().shape({
  fechaDocumento: Yup.string().required(),
  glosa: Yup.string().required()
})

const MainForm = ({ resetMainForm, editData, isLargeDataset = false, asientoId = null }) => {
  const __zariContext = React.useContext(ZariContext)
  const __adminContext = React.useContext(AdminContext)
  const __periodoContext = React.useContext(PeriodoContext)

  const location = useLocation()

  const [plantillaEncontrada, setPlantillaEncontrada] = React.useState(false)
  const [mostrarPartida, setMostrarPartida] = React.useState(false)
  const [checkedActualizar, setCheckedActualizar] = React.useState(false)
  const [diferenciaSoles, setDiferenciaSoles] = React.useState(0)

  const [changelog, setChangelog] = React.useState({
    modificados: new Map(),
    eliminados: [],
    nuevos: new Map()
  })

  const [currentPage, setCurrentPage] = React.useState(1)
  const [isLoadingMore, setIsLoadingMore] = React.useState(false)
  const [hasMore, setHasMore] = React.useState(true)
  const pageSize = 50

  const [detalles, detallesDispatcher] = React.useReducer(commonReducer, editData.detalles, innerDetails => {
    if (innerDetails) {
      return innerDetails.map(x => ({
        ...x,
        _idGlosa: x.glosa || '',
        _idFila: IdGen.newId(),
        modalValido: true
      }))
    } else {
      return []
    }
  })

  const mainFormikRef = React.useRef(null)

  React.useEffect(() => {
    if (isLargeDataset && asientoId && detalles.length === 0) {
      setIsLoadingMore(true)
      AsientosContablesService.getDetallesPaginado(asientoId, 1, pageSize)
        .then(({ data }) => {
          detallesDispatcher({ type: 'append', payload: data.detalles })
          setCurrentPage(1)
          setHasMore(data.detalles.length === pageSize)
        })
        .catch(({ data: { error } }) => {
          __zariContext.snack('error', error)
        })
        .finally(() => {
          setIsLoadingMore(false)
        })
    }
  }, [isLargeDataset, asientoId, __zariContext])

  const registrarModificacion = React.useCallback(
    detalle => {
      if (!isLargeDataset) return

      setChangelog(prev => {
        const next = { ...prev }
        if (detalle._esNuevo) {
          const nuevosMap = new Map(prev.nuevos)
          nuevosMap.set(detalle._idFila, detalle)
          next.nuevos = nuevosMap
        } else {
          const modMap = new Map(prev.modificados)
          modMap.set(detalle._id, detalle)
          next.modificados = modMap
        }
        return next
      })
    },
    [isLargeDataset]
  )

  const registrarEliminacion = React.useCallback(
    detalle => {
      if (!isLargeDataset) return

      setChangelog(prev => {
        const next = { ...prev }
        if (detalle._esNuevo) {
          const nuevosMap = new Map(prev.nuevos)
          nuevosMap.delete(detalle._idFila)
          next.nuevos = nuevosMap
        } else {
          next.eliminados = [...prev.eliminados, detalle._id]
          const modMap = new Map(prev.modificados)
          modMap.delete(detalle._id)
          next.modificados = modMap
        }
        return next
      })
    },
    [isLargeDataset]
  )

  const loadMore = React.useCallback(() => {
    if (isLoadingMore || !hasMore || !isLargeDataset) return

    setIsLoadingMore(true)
    const nextPage = currentPage + 1

    AsientosContablesService.getDetallesPaginado(asientoId, nextPage, pageSize)
      .then(({ data }) => {
        detallesDispatcher({ type: 'append', payload: data.detalles })
        setCurrentPage(nextPage)
        setHasMore(data.detalles.length === pageSize)
      })
      .catch(({ data: { error } }) => {
        __zariContext.snack('error', error)
      })
      .finally(() => {
        setIsLoadingMore(false)
      })
  }, [isLoadingMore, hasMore, isLargeDataset, currentPage, asientoId, __zariContext])

  React.useEffect(() => {
    if (editData._id) {
      setPlantillaEncontrada(true)
    }
  }, [editData])

  const formInitialValues = React.useMemo(() => {
    let { origen } = queryString.parse(location.search)

    origen = origen || OrigenesContabilidadConstant.DIARIO

    const plantilla = origen !== OrigenesContabilidadConstant.DIARIO ? 'NO_ID' : ''

    return {
      _id: editData._id || '',
      _idPartida: editData._idPartida || '',
      _idPlantilla: editData._idPlantilla || plantilla,
      fechaDocumento: editData.fechaDocumento || '',
      glosa: editData.glosa || '',
      origen: origen,
      tipoCambio: editData.tipoCambio || '',
      numeroOperacion: editData.numeroOperacion || ''
    }
  }, [editData, location.search])

  const clickContinuar = React.useCallback(
    ({ _idPlantilla, tipoCambio, glosa, _idGlosa }) => {
      if (!tipoCambio || tipoCambio === 1) {
        __zariContext.snack('error', 'Ingresar tipo de cambio.')
        return
      }

      if (_idPlantilla === 'NO_ID') {
        setPlantillaEncontrada(true)
        detallesDispatcher({
          type: 'agregar',
          payload: { tipoCambio, glosa, _idGlosa }
        })
      } else {
        PlantillaGeneralService.getxIdAsDetalles(_idPlantilla).then(({ data }) => {
          if (data) {
            const newData = data.map(x => ({
              ...x,
              moneda: x.cuentaContableMoneda,
              tipoCambio: tipoCambio,
              glosa: glosa,
              _idGlosa: _idGlosa
            }))

            detallesDispatcher({ type: 'fill', payload: newData })
            setPlantillaEncontrada(true)
            KeyEventUtil.leaveOnKeyEvent('input-det-importeDebe-0')
          }
        })
      }
    },
    [__zariContext]
  )

  const onSubmit = React.useCallback(
    (data, { setSubmitting }) => {
      const arrFecha = data.fechaDocumento.split('/')
      const mesDocumento = parseInt(arrFecha[1])
      const mesTrabajo = parseInt(__adminContext.mesTrabajo)
      if (mesTrabajo !== 0 && mesTrabajo !== 14 && mesTrabajo !== 15) {
        if (mesDocumento !== mesTrabajo) {
          ZrSwal.showAlert({
            tipo: 'error',
            mensaje: 'Fecha de documento es diferente al mes de contabilidad.',
            onSuccess: () => setSubmitting(false)
          })
          return
        }
      }

      const ejecutarGuardado = () => {
        let formData
        let serviceFn

        if (isLargeDataset && data._id) {
          const nuevosArray = Array.from(changelog.nuevos.values()).filter(x => x.importeDebe || x.importeHaber)
          const modificadosArray = Array.from(changelog.modificados.values()).filter(
            x => x.importeDebe || x.importeHaber
          )

          if (nuevosArray.length === 0 && modificadosArray.length === 0 && changelog.eliminados.length === 0) {
            __zariContext.snack('info', 'No se detectaron cambios.')
            setSubmitting(false)
            return
          }

          const todosActivos = [...nuevosArray, ...modificadosArray]
          for (let i = 0; i < todosActivos.length; i++) {
            const element = todosActivos[i]
            if (!element.importeDebe && !element.importeHaber) {
              __zariContext.snack('error', 'Verificar montos ingresados en los detalles.')
              setSubmitting(false)
              return
            }
            if (!element.cuentaContable) {
              __zariContext.snack('error', 'Verificar cuentas contables ingresadas.')
              setSubmitting(false)
              return
            }
          }

          formData = {
            ...data,
            flagActualizarPlantilla: checkedActualizar,
            nuevos: nuevosArray,
            modificados: modificadosArray,
            eliminados: changelog.eliminados
          }
          serviceFn = AsientosContablesService.editarConChangelog
        } else {
          const nuevosDetalles = [...detalles].filter(x => x.importeDebe || x.importeHaber)

          for (let i = 0; i < nuevosDetalles.length; i++) {
            const element = nuevosDetalles[i]
            if (!element.importeDebe && !element.importeHaber) {
              __zariContext.snack('error', 'Verificar montos ingresados en los detalles.')
              setSubmitting(false)
              return
            }
            if (!element.cuentaContable) {
              __zariContext.snack('error', 'Verificar cuentas contables ingresadas.')
              setSubmitting(false)
              return
            }
          }

          formData = {
            ...data,
            flagActualizarPlantilla: checkedActualizar,
            detalles: nuevosDetalles
          }
          serviceFn = AsientosContablesService.nuevoAsientoContable2
        }

        if (!mostrarPartida) {
          formData._idPartida = ''
        } else {
          if (!formData._idPartida) {
            ZrSwal.showAlert({
              tipo: 'error',
              mensaje: 'Seleccionar partida.',
              onSuccess: () => setSubmitting(false)
            })
            return
          }
        }

        serviceFn(formData)
          .then(({ data }) => {
            ZrSwal.showAlert({
              tipo: 'success',
              mensaje: (
                <>
                  <span>Operaciones Generadas :</span> <br />
                  {data.operacionesGeneradas.map((x, k) => (
                    <React.Fragment key={k}>
                      <span>
                        {k + 1}. {x}
                      </span>{' '}
                      <br />
                    </React.Fragment>
                  ))}
                </>
              ),
              onSuccess: () => {
                resetMainForm()
              }
            })
          })
          .catch(({ data: { error } }) => {
            __zariContext.snack('error', error)
            setSubmitting(false)
          })
          .finally(() => {
            __periodoContext.actualizarEstados()
          })
      }

      if (diferenciaSoles !== 0) {
        ZrSwal.showAlert({
          tipo: 'warning',
          mensaje: 'Se encontro un descuadre en soles, ¿Desea continuar?',
          showCerrar: true,
          onSuccess: () => ejecutarGuardado(),
          onClose: () => setSubmitting(false)
        })
      } else {
        ejecutarGuardado()
      }
    },
    [
      detalles,
      changelog,
      isLargeDataset,
      __zariContext,
      __periodoContext,
      mostrarPartida,
      checkedActualizar,
      resetMainForm,
      diferenciaSoles,
      __adminContext
    ]
  )

  const clickGuardar = React.useCallback(() => {
    mainFormikRef.current.handleSubmit()
  }, [])

  return (
    <>
      <Formik
        initialValues={formInitialValues}
        innerRef={mainFormikRef}
        validationSchema={ValidationSchema}
        onSubmit={onSubmit}
      >
        {({ errors, touched, isSubmitting, values, setFieldValue, setValues }) => (
          <Form>
            <Row>
              {values._id ? (
                <Col md={3}>
                  <FormGroups.NumeroOperacionFormGroup iValue={values.numeroOperacion} />
                </Col>
              ) : (
                <>
                  <Col md={3}>
                    <FormGroups.PlantillaFormGroup
                      fIsSubmitting={isSubmitting || plantillaEncontrada}
                      iError={errors._idPlantilla}
                      iTouched={touched._idPlantilla}
                      vOrigen={values.origen}
                      setFieldValue={setFieldValue}
                    />
                  </Col>
                </>
              )}
              <Col md={3}>
                <FormGroups.GlosaFormGroup
                  fIsSubmitting={isSubmitting}
                  iError={errors.glosa}
                  iTouched={touched.glosa}
                />
              </Col>
              {mostrarPartida && (
                <Col md={3}>
                  <FormGroups.TrabajadorFormGroup
                    fIsSubmitting={isSubmitting}
                    iError={errors._idTrabajador}
                    iTouched={touched._idTrabajador}
                    iValue={values.nombreTrabajador}
                    setValues={setValues}
                    values={values}
                  />
                </Col>
              )}
              <Col md={2}>
                <Form.Checkbox
                  id='checkbox-activarfondofijo'
                  name='checkbox-activarfondofijo'
                  label='Activar Fondo Fijo'
                  checked={mostrarPartida}
                  onChange={() => setMostrarPartida(prev => !prev)}
                />
              </Col>
            </Row>
            <Row>
              <Col md={3}>
                <FormGroups.FechaDocumentoFormGroup
                  fIsSubmitting={isSubmitting}
                  iError={errors.fechaDocumento}
                  iTouched={touched.fechaDocumento}
                />
              </Col>
              {!values._id && (
                <Col md={3}>
                  <FormGroups.TipoCambioFormGroup
                    iError={errors.tipoCambio}
                    iFechaDocumento={values.fechaDocumento}
                    iTouched={touched.tipoCambio}
                    setFieldValue={setFieldValue}
                  />
                </Col>
              )}
              {mostrarPartida && (
                <Col md={3}>
                  <FormGroups.PartidaFormGroup
                    fIsSubmitting={isSubmitting}
                    iError={errors._idPartida}
                    iTouched={touched._idPartida}
                    vSocio={values._idTrabajador}
                  />
                </Col>
              )}
              {!plantillaEncontrada && (
                <>
                  <Col md={2}>
                    <Button id='button-continuar' onClick={() => clickContinuar(values)} block>
                      Continuar
                    </Button>
                  </Col>
                </>
              )}
            </Row>
            {plantillaEncontrada && (
              <>
                <Divider />
                <Sections.ResumenMontosSection
                  detalles={detalles}
                  diferenciaSoles={diferenciaSoles}
                  setDiferenciaSoles={setDiferenciaSoles}
                  dispatch={detallesDispatcher}
                  clickGuardar={clickGuardar}
                  vId={values._id}
                  resetMainForm={resetMainForm}
                  checkedCheckbox={checkedActualizar}
                  onChangeCheckbox={() => setCheckedActualizar(prev => !prev)}
                  isSubmitting={isSubmitting}
                  resumenTotales={isLargeDataset ? editData.resumen : null}
                />
                <Divider />
                <Sections.DetallesTablaSection
                  detalles={detalles}
                  setDiferenciaSoles={setDiferenciaSoles}
                  dispatch={detallesDispatcher}
                  vOrigen={values.origen}
                  vTipoCambio={values.tipoCambio}
                  vGlosa={values.glosa}
                  vIdGlosa={values._idGlosa}
                  setFieldValue={setFieldValue}
                  isLargeDataset={isLargeDataset}
                  loadMore={loadMore}
                  isLoadingMore={isLoadingMore}
                  hasMore={hasMore}
                  totalRegistros={editData.resumen?.totalRegistros}
                  asientoId={asientoId}
                  registrarModificacion={registrarModificacion}
                  registrarEliminacion={registrarEliminacion}
                />
              </>
            )}
          </Form>
        )}
      </Formik>
    </>
  )
}

export default React.memo(MainForm)
